import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Lock, Save, ShieldCheck, AlertTriangle, CheckCircle2, FlaskConical, Search, Radio, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

/**
 * Owner-only platform Stripe configuration (SuperAdmin → More → Stripe Config).
 * Two independent credential sets: Production (default for all orgs) and Sandbox
 * (only the orgs you assign use these test keys). Secrets are write-only from the
 * UI and stored AES-256-GCM encrypted server-side.
 */
const StripeConfigTab = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/super-admin/stripe-config')
      .then(({ data }) => setCfg(data))
      .catch(() => toast.error('Failed to load Stripe config (Owner access required).'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div data-testid="stripe-config-loading" className="p-6 text-sm text-slate-500">Loading Stripe configuration…</div>;
  }

  return (
    <div data-testid="stripe-config-tab" className="max-w-2xl space-y-6 p-1">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-indigo-600 p-2.5 text-white"><CreditCard className="h-5 w-5" /></div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Stripe Configuration</h2>
          <p className="text-sm text-slate-500">
            Production keys are the default for every org. Assign specific orgs to Sandbox to test them with test payments.
          </p>
        </div>
      </div>

      <Tabs defaultValue="prod">
        <TabsList className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="prod" data-testid="stripe-tab-prod">
            <ShieldCheck className="mr-1.5 h-4 w-4" /> Production
          </TabsTrigger>
          <TabsTrigger value="sandbox" data-testid="stripe-tab-sandbox">
            <FlaskConical className="mr-1.5 h-4 w-4" /> Sandbox
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prod" className="mt-5 space-y-6">
          <WebhookHealth health={cfg?.webhook_health} onRefresh={load} />
          <EnvForm env="prod" data={cfg?.prod} onSaved={setCfg} />
        </TabsContent>

        <TabsContent value="sandbox" className="mt-5 space-y-6">
          <EnvForm env="sandbox" data={cfg?.sandbox} onSaved={setCfg} />
          <SandboxOrgs sandboxOrgIds={cfg?.sandbox_org_ids || []} onSaved={setCfg} />
        </TabsContent>
      </Tabs>

      {cfg?.updated_at && (
        <p className="text-[11px] text-slate-400">Last updated {new Date(cfg.updated_at).toLocaleString()}{cfg.updated_by ? ` by ${cfg.updated_by}` : ''}.</p>
      )}
    </div>
  );
};

const _timeAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const WebhookHealth = ({ health, onRefresh }) => {
  const received = !!health?.last_received_at;
  const failed = health?.last_status === 'signature_failed';
  const ok = received && !failed;
  const tone = !received ? 'slate' : ok ? 'emerald' : 'rose';
  const toneCls = {
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
  }[tone];

  return (
    <div data-testid="stripe-webhook-health" className={`rounded-2xl border p-4 ${toneCls}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Radio className={`mt-0.5 h-4 w-4 shrink-0 ${!received ? 'text-slate-400' : ok ? 'text-emerald-600' : 'text-rose-600'}`} />
          <div>
            <div className="flex items-center gap-2 text-sm font-bold">
              Webhook status
              <span
                data-testid="stripe-webhook-health-state"
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  !received ? 'bg-slate-200 text-slate-600' : ok ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}
              >
                {!received ? 'NO EVENTS YET' : ok ? 'RECEIVING' : 'SIGNATURE FAILING'}
              </span>
            </div>
            {received ? (
              <div className="mt-1 space-y-0.5 text-[12px]">
                <div data-testid="stripe-webhook-health-last">
                  Last event: <span className="font-semibold">{health.last_event_type || '—'}</span>
                  {health.last_env ? ` (${health.last_env}${health.last_livemode === false ? ' · test' : ''})` : ''} · {_timeAgo(health.last_received_at)}
                </div>
                {health.last_verified_at && (
                  <div>Last verified OK: {_timeAgo(health.last_verified_at)}</div>
                )}
                <div className="text-[11px] opacity-80">
                  {(health.verified_count || 0)} verified · {(health.failed_count || 0)} failed · {(health.total_received || 0)} total
                </div>
                {failed && health.last_error && (
                  <div className="mt-1 rounded-md bg-rose-100 px-2 py-1 text-[11px] text-rose-800">
                    Latest failure: {health.last_error}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-1 text-[12px]">
                No Stripe webhook has reached this endpoint yet. Once Stripe delivers to
                <code className="mx-1 rounded bg-white/60 px-1">/api/stripe/webhook</code>, its status shows here.
              </div>
            )}
          </div>
        </div>
        <button
          data-testid="stripe-webhook-health-refresh"
          onClick={onRefresh}
          className="inline-flex items-center gap-1 rounded-lg border border-current/20 px-2.5 py-1 text-[12px] font-semibold opacity-80 transition-opacity hover:opacity-100"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
    </div>
  );
};

const EnvForm = ({ env, data, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ publishable_key: '', secret_key: '', webhook_secret: '' });
  const isProd = env === 'prod';
  const live = data?.mode === 'live';

  useEffect(() => {
    setForm((f) => ({ ...f, publishable_key: data?.publishable_key || '' }));
  }, [data?.publishable_key]);

  const save = () => {
    setSaving(true);
    const payload = { env, publishable_key: form.publishable_key };
    if (form.secret_key.trim()) payload.secret_key = form.secret_key.trim();
    if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
    api.put('/super-admin/stripe-config', payload)
      .then(({ data: fresh }) => {
        onSaved(fresh);
        setForm({ publishable_key: fresh?.[env]?.publishable_key || '', secret_key: '', webhook_secret: '' });
        toast.success(`${isProd ? 'Production' : 'Sandbox'} Stripe keys saved.`);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to save Stripe config.'))
      .finally(() => setSaving(false));
  };

  const isReal = data?.active_provider === 'stripe';

  return (
    <div className="space-y-5">
      {/* Status */}
      <div data-testid={`stripe-config-status-${env}`} className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Active source</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {data?.source === 'database'
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Database (custom keys)</>
              : <><ShieldCheck className="h-4 w-4 text-slate-500" /> Environment</>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Mode</div>
          <div className={`mt-0.5 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-bold ${live ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
            {live ? 'LIVE' : 'TEST'}
          </div>
        </div>
      </div>

      {isProd && live && (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[13px] text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Live mode — real cards will be charged for all production orgs. Ensure keys and webhook secret are correct.
        </div>
      )}

      <div
        data-testid={`stripe-active-provider-${env}`}
        className={`flex items-start gap-2 rounded-xl border p-3 text-[13px] ${isReal ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
      >
        {isReal ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <div>
          <div className="font-semibold">
            {isReal
              ? `Checkout uses YOUR Stripe key — recurring subscriptions (${env})`
              : 'Falling back to the built-in Emergent sandbox — one-time test payments'}
          </div>
          <div className="mt-0.5">
            {isReal
              ? `Checkouts for ${isProd ? 'production orgs' : 'orgs assigned to sandbox'} create a real auto-renewing monthly subscription.`
              : `Save a ${isProd ? 'live' : 'test'} Secret/Restricted key below. Until then, ${isProd ? 'production' : 'assigned'} orgs use the Emergent test checkout.`}
          </div>
          {isReal && !data?.has_webhook_secret && (
            <div className="mt-1 text-amber-700">
              ⚠ No webhook signing secret set — add your <code>whsec_…</code> below so subscription renewals/cancellations sync back.
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
        <Field label={`${isProd ? 'Live' : 'Test'} Secret key (sk_…) or Restricted key (rk_…) — required`} hint={data?.has_secret_key ? `Saved: ${data.secret_key_masked}. Leave blank to keep.` : `Use a ${isProd ? 'live' : 'test'} Restricted key (rk_…, scope: Checkout Sessions + Subscriptions + Prices) or full Secret key (sk_…).`} lock>
          <input
            data-testid={`stripe-secret-key-${env}`}
            type="password"
            value={form.secret_key}
            onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
            placeholder={data?.has_secret_key ? '•••••••• (unchanged)' : (isProd ? 'rk_live_… / sk_live_…' : 'rk_test_… / sk_test_…')}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
        </Field>

        <Field label="Webhook signing secret (whsec_…)" hint={data?.has_webhook_secret ? `Saved: ${data.webhook_secret_masked}. Leave blank to keep.` : 'Recommended — verifies Stripe callbacks for renewals/cancellations.'} lock>
          <input
            data-testid={`stripe-webhook-secret-${env}`}
            type="password"
            value={form.webhook_secret}
            onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
            placeholder={data?.has_webhook_secret ? '•••••••• (unchanged)' : 'whsec_…'}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
        </Field>

        <Field label="Publishable key (pk_…) — optional" hint="BillPortal uses Stripe hosted checkout, which does NOT need this. Leave blank.">
          <input
            data-testid={`stripe-publishable-key-${env}`}
            value={form.publishable_key}
            onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
            placeholder={isProd ? 'pk_live_… (optional)' : 'pk_test_… (optional)'}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
        </Field>

        <div className="flex items-center justify-end pt-1">
          <button
            data-testid={`stripe-config-save-${env}`}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />{saving ? 'Saving…' : `Save ${isProd ? 'Production' : 'Sandbox'} keys`}
          </button>
        </div>
      </div>
    </div>
  );
};

const SandboxOrgs = ({ sandboxOrgIds, onSaved }) => {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(new Set(sandboxOrgIds));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setSelected(new Set(sandboxOrgIds)); }, [sandboxOrgIds]);

  useEffect(() => {
    api.get('/super-admin/stripe-config/orgs')
      .then(({ data }) => setOrgs(data || []))
      .catch(() => toast.error('Failed to load orgs.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = () => {
    setSaving(true);
    api.put('/super-admin/stripe-config/sandbox-orgs', { org_ids: Array.from(selected) })
      .then(({ data }) => { onSaved(data); toast.success('Sandbox org assignment saved.'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to save assignment.'))
      .finally(() => setSaving(false));
  };

  const filtered = orgs.filter((o) =>
    !search || (o.name || '').toLowerCase().includes(search.toLowerCase()) || o.id.includes(search));

  return (
    <div data-testid="sandbox-orgs" className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
          <FlaskConical className="h-4 w-4 text-amber-600" /> Orgs using Sandbox payments
        </h3>
        <p className="mt-0.5 text-[12px] text-slate-500">
          Selected orgs check out with the Sandbox keys above. Everyone else uses Production. ({selected.size} selected)
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="sandbox-org-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search orgs…"
          className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-indigo-400 focus:outline-none"
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {loading ? (
          <div className="p-4 text-sm text-slate-400">Loading orgs…</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-slate-400">No orgs match.</div>
        ) : filtered.map((o) => (
          <label
            key={o.id}
            data-testid={`sandbox-org-option-${o.id}`}
            className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              checked={selected.has(o.id)}
              onChange={() => toggle(o.id)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="flex-1 truncate text-slate-800">{o.name}</span>
            {selected.has(o.id) && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">SANDBOX</span>}
          </label>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          data-testid="sandbox-orgs-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />{saving ? 'Saving…' : 'Save org assignment'}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, hint, lock, children }) => (
  <div>
    <label className="flex items-center gap-1 text-[13px] font-semibold text-slate-700">
      {lock && <Lock className="h-3 w-3 text-slate-400" />}{label}
    </label>
    {children && <div className="mt-1">{children}</div>}
    {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
  </div>
);

export default StripeConfigTab;
